import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon, SearchIcon } from "lucide-react";
import { topNavItems, MenuKey, ViewMode } from "../data/navigation";
import { I9Logo } from "./I9Logo";
import { BrandLabel } from "./BrandLabel";
import { NoteTag } from "./NoteTag";
import { WhyMegaMenu } from "./WhyMegaMenu";
import { CurrentAboutMenu } from "./CurrentAboutMenu";
import { ProgramsMenu } from "./ProgramsMenu";
import { ContactMenu } from "./ContactMenu";
type SiteHeaderProps = {
  view: ViewMode;
  showNotes: boolean;
  openMenu: MenuKey | null;
  onToggleMenu: (key: MenuKey) => void;
};
const menuPlacement: Record<MenuKey, string> = {
  programs: "right-[190px] w-[min(900px,calc(100%-2rem))]",
  brand: "right-4 w-[min(1000px,calc(100%-2rem))]",
  contact: "right-4 w-[520px]"
};
const menuNames: Record<MenuKey, string> = {
  programs: "Programs",
  brand: "Why i9 Sports",
  contact: "Contact"
};
export function SiteHeader({
  view,
  showNotes,
  openMenu,
  onToggleMenu
}: SiteHeaderProps) {
  const base = "relative flex h-full items-center gap-1 whitespace-nowrap px-4 text-[14px] font-semibold uppercase transition-colors duration-150 ease-out";
  return <div className="relative z-20" data-id="element-181" data-mp="2jj1dbir4epa2">
      <header className="relative flex h-16 items-stretch justify-end bg-white pl-44 pr-4" data-id="element-182" data-mp="2jfwyzsjxyd5o">
        <a href="#" className="absolute left-4 top-2 z-10" aria-label="i9 Sports home" data-id="element-183" data-mp="2ogb7fbpgfp48">
          <I9Logo data-id="element-184" data-mp="1quvr0rkf4s84" />
        </a>
        <nav aria-label="Main" className="flex items-stretch" data-id="element-185" data-mp="1qrhptmnhqp29">
          <ul className="flex items-stretch" data-id="element-186" data-mp="1vsx7d4qgmdfw">
            {topNavItems.map(item => {
            const isBrand = item.label === "__brand__";
            const label = isBrand ? <BrandLabel view={view} data-id="element-187" data-mp="25igpu9a9g5a8" /> : item.label;
            if (item.menuKey) {
              const key = item.menuKey;
              const active = openMenu === key;
              return <li key={item.label} className="flex" data-id="element-188" data-mp="1d0w2nlr07syk">
                    <button type="button" onClick={() => onToggleMenu(key)} aria-expanded={active} aria-haspopup="true" className={`${base} ${active ? "bg-i9-blue text-white" : "text-i9-navy hover:bg-i9-mist"}`} data-id="element-189" data-mp="15185mf0z7e0r">
                      {showNotes && view === "proposed" && isBrand && <NoteTag className="absolute left-1/2 top-1 -translate-x-1/2" data-id="element-190" data-mp="m1ua3snllv85">Renamed</NoteTag>}
                      {label}
                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${active ? "rotate-180" : ""}`} data-id="element-191" data-mp="h5dlkxchleuj" />
                    </button>
                  </li>;
            }
            return <li key={item.label} className="flex" data-id="element-192" data-mp="27dvo1a50iu00">
                  <a href="#" className={`${base} text-i9-navy hover:bg-i9-mist`} data-id="element-193" data-mp="22h5aatgokahr">
                    {label}
                    {item.hasMenu && <ChevronDownIcon className="h-4 w-4" data-id="element-194" data-mp="29zd90jc31opm" />}
                  </a>
                </li>;
          })}
          </ul>
        </nav>
      </header>

      <div className="flex h-10 items-center justify-between bg-i9-navy pl-48 text-white" data-id="element-195" data-mp="21089b9osbpbv">
        <div className="flex items-center gap-3" data-id="element-196" data-mp="250icyfp3u3nk">
          <span className="text-[12px] font-bold uppercase tracking-wide" data-id="element-197" data-mp="251bnlrr6pdaz">Find a program near you</span>
          <div className="flex" data-id="element-198" data-mp="2ssphi24zhgy2">
            <input aria-label="ZIP code" defaultValue="33578" className="h-7 w-24 bg-white px-2 text-sm text-i9-ink" data-id="element-199" data-mp="2xpfv8itbg0gb" />
            <button aria-label="Search programs" className="flex h-7 w-8 items-center justify-center bg-i9-red" data-id="element-200" data-mp="2jz40sd99dpm0">
              <SearchIcon className="h-3.5 w-3.5" data-id="element-201" data-mp="2nyl9dfjzf2ve" />
            </button>
          </div>
        </div>
        <div className="flex h-full items-center" data-id="element-202" data-mp="3boozql85t64g">
          <a href="#" className="flex items-center gap-1 px-4 text-[12px] font-medium" data-id="element-203" data-mp="3gg731fxnnisl">
            Login / Sign up <ChevronDownIcon className="h-3.5 w-3.5" data-id="element-204" data-mp="1srlz5hvxb13s" />
          </a>
          <a href="#" className="flex h-full items-center bg-i9-red px-6 text-[12px] font-medium" style={{
          clipPath: "polygon(12% 0, 100% 0, 100% 100%, 0 100%)"
        }} data-id="element-205" data-mp="1st6rd0urm7yh">
            Franchise Info
          </a>
        </div>
      </div>

      <AnimatePresence mode="wait" data-id="element-206" data-mp="1etfi1df71fqt">
        {openMenu && <motion.div key={`${openMenu}-${view}`} initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className={`absolute top-16 ${menuPlacement[openMenu]}`} role="region" aria-label={`${openMenu === "brand" && view === "current" ? "About Us" : menuNames[openMenu]} menu`} data-id="element-207" data-mp="2biwmxq6cqxrg">
            {openMenu === "programs" && <ProgramsMenu view={view} showNotes={showNotes} data-id="element-208" data-mp="2phwbkxtv7cnm" />}
            {openMenu === "contact" && <ContactMenu view={view} showNotes={showNotes} data-id="element-209" data-mp="c01neqwpojz0" />}
            {openMenu === "brand" && (view === "proposed" ? <WhyMegaMenu showNotes={showNotes} data-id="element-210" data-mp="gzmmd1bvssbq" /> : <CurrentAboutMenu showNotes={showNotes} data-id="element-211" data-mp="h0pf7esrl4ua" />)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}